import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isNative } from '@/lib/native/platform';
import { initDeepLinks } from '@/lib/native/deepLinks';
import { installExternalLinkInterceptor } from '@/lib/native/externalLinkInterceptor';
import { checkMinVersion, type VersionGateResult } from '@/lib/native/versionGate';
import { initAppLock, subscribe as subscribeAppLock, unlock } from '@/lib/native/appLock';
import { useAuth } from '@/hooks/useAuth';
import { useReconnectDrain } from '@/hooks/useReconnectDrain';
import { drainCaptureQueue } from '@/lib/capture/drainCaptures';
import { drainVoiceNotes, initVoiceNoteCaptureBridge } from '@/lib/native/voiceNoteCapture';
import { registerDeviceSession } from '@/lib/native/deviceSession';
import { reconcileSessionOnResume } from '@/lib/native/sessionBridge';
import { enablePushNotifications, setNavigate } from '@/lib/native/push';
import { OfflineBanner } from '@/components/OfflineBanner';
import { AppLockOverlay } from './AppLockOverlay';
import { BottomTabBar } from './BottomTabBar';
import { ProfileSheet } from './ProfileSheet';

// Global mobile chrome (P0-IA-05). Mounted ONCE near the app root (a sibling of <Routes>, inside the
// Router) so it overlays every authenticated screen regardless of which Layout a route uses — several
// /admin/* routes render outside <Layout>, so the shell cannot rely on Layout.
//
// Active ONLY when running as the native app (isNative()). The public website — including phone
// browsers, even with a persisted staff session — keeps its desktop Layout/PartnerNavbar and never
// renders this shell, so capeberkshire.co.uk no longer mirrors the app in a mobile browser. Deep-link
// + forced-update handling are native-only concerns.
export function MobileShell() {
  const native = isNative();
  const active = native;
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading } = useAuth();
  const [gate, setGate] = useState<VersionGateResult>({ blocked: false });

  // Biometric app-lock (P0-SEC-02). Native only — web never initialises the lock, never loads the
  // plugin chunk, and `locked` stays false so nothing below changes for the website build.
  // Start locked on native (matches initAppLock's lockOnStart default) so the very first paint of a
  // cold start is the overlay, never content. The init effect below subscribes and syncs this state
  // with the appLock module immediately after mount (subscribe fires with the current value).
  const [locked, setLocked] = useState<boolean>(native);
  const [lockFailed, setLockFailed] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const unlockInFlight = useRef(false);

  // Single-flight biometric attempt. FAIL CLOSED: on cancel/failure the lock stays and we flip
  // lockFailed so the overlay shows the message + "Try again". appLock.unlock() itself fails open
  // (resolves true) only when the device genuinely has nothing to authenticate with.
  const tryUnlock = useCallback(async () => {
    if (unlockInFlight.current) return;
    unlockInFlight.current = true;
    setLockBusy(true);
    setLockFailed(false);
    try {
      const ok = await unlock();
      if (!ok) setLockFailed(true);
    } finally {
      unlockInFlight.current = false;
      setLockBusy(false);
    }
  }, []);

  // Initialise the lock manager once on native: lockOnStart (cold-start gate) + re-lock when the app
  // returns from background past the grace period (appLock listens to @capacitor/app appStateChange
  // with its built-in 60s timeout). Web: effect no-ops, nothing is initialised or imported.
  useEffect(() => {
    if (!native) return;
    const dispose = initAppLock();
    const unsub = subscribeAppLock(setLocked);
    return () => {
      unsub();
      dispose();
    };
  }, [native]);

  // Auto-prompt whenever we become locked (cold start + each re-lock on resume). A failed/cancelled
  // attempt leaves `locked` true without re-firing this effect, so there is no prompt loop — the
  // user retries via the overlay button.
  useEffect(() => {
    if (!native || !locked) return;
    void tryUnlock();
  }, [native, locked, tryUnlock]);

  // Global reconnect/foreground trigger: refetch active React Query data AND drain the offline
  // capture queue when the network returns or the app is foregrounded. Called unconditionally
  // (rules-of-hooks). drainCaptureQueue self-guards (no-op when signed out / queue empty / offline),
  // so it's harmless on the desktop marketing site; the stable useCallback keeps the listener from
  // re-subscribing each render.
  const onReconnect = useCallback(() => {
    void drainCaptureQueue();
    // In-car voice notes (FEATURE 3b): fold any tile/notification captures into the upload pipeline on
    // reconnect/resume too. Native-only, single-flight, no-op on web / when nothing is pending.
    void drainVoiceNotes();
    // Refresh / retry the device-session row on reconnect (native-only, self-guarding, idempotent upsert)
    // so a registration that failed while offline recovers and last_seen_at/app_version stay current.
    void registerDeviceSession();
  }, []);
  useReconnectDrain({ onReconnect });

  // In-car voice-note bridge (FEATURE 3b): subscribe to the native "voiceNoteCaptured" event so a note
  // finished from the quick-settings tile / notification while the app is foreground uploads immediately,
  // and run one catch-up drain for notes captured while the app was killed. Native-only, self-guarding;
  // no-op on web. Mounted once here alongside the other native init effects.
  useEffect(() => {
    if (!native) return;
    return initVoiceNoteCaptureBridge();
  }, [native]);

  // Cold-start / login drain for in-car voice notes, mirroring the capture-queue drain below: pick up
  // any tile/notification captures recorded in a previous (killed) session once a signed-in native
  // session is present. Self-guarding + single-flight, so it's safe alongside the bridge init above.
  useEffect(() => {
    if (!native || !isAuthenticated) return;
    void drainVoiceNotes();
  }, [native, isAuthenticated]);

  // Live session link (WS-LINK, plan §5): the resume-triggered reconciliation is wired inside
  // useReconnectDrain's own 'resume' listener above (native→WebView adoption of a token rotated by
  // e.g. Android Auto while the WebView was backgrounded). Also run it once on cold mount — a resume
  // event never fires for a fresh app launch, so a session written natively before the WebView first
  // starts (or while it was fully killed) is otherwise only picked up lazily. Native-only, self-guarding,
  // never throws; no-ops on web.
  useEffect(() => {
    if (!native) return;
    void reconcileSessionOnResume();
  }, [native]);

  // Cold-start / login drain: useReconnectDrain only fires on network *changes* and app resume, so a
  // capture queued in a previous session (app killed offline, reopened online) would otherwise sit
  // until the next reconnect event. Kick one drain when an authenticated session is present. The
  // drain self-guards (single-flight + no-op when offline / queue empty), so this is safe to fire
  // alongside the reconnect trigger and on the desktop site (where the queue is always empty).
  useEffect(() => {
    if (!active || !isAuthenticated) return;
    void drainCaptureQueue();
  }, [active, isAuthenticated]);

  // Device-session registration (Phase 1 remote-wipe backbone): once an authenticated native session is
  // present, register/refresh this device's session row so it can later be listed and remote-wiped, and
  // so the wipe handler can self-identify it. Gated on `native` (NOT `active` — registration is a
  // native-device concern; `active` is also true on phone web). Self-guarding + idempotent (the RPC
  // upserts on (uid, device_id)) and never throws, so it's safe to fire alongside the drain trigger; the
  // desktop/web build no-ops behind isNative(). Reconnect re-runs it via the onReconnect callback above.
  useEffect(() => {
    if (!native || !isAuthenticated) return;
    void registerDeviceSession();
  }, [native, isAuthenticated]);

  // Native push registration (P0-PUSH-02) + remote-wipe delivery (P1). Without this, the push module's
  // listeners (pushNotificationReceived / pushNotificationActionPerformed) never attach, so a silent
  // `data.command === 'wipe'` push is ignored and the remote wipe never fires on the device. Mirrors the
  // registerDeviceSession() effect: gated on `native && isAuthenticated` (registration is a native-device
  // concern and the token upsert needs an authenticated user). Wire the router first so a tapped
  // notification can navigate, then enable push (idempotent — listeners are reset on each call, and the
  // module tears its own listeners down on the next sign-out via removeListeners). Self-guarding + never
  // throws; web no-ops behind isNative() so the desktop/web build is unaffected.
  useEffect(() => {
    if (!native || !isAuthenticated) return;
    setNavigate(navigate);
    void enablePushNotifications();
  }, [native, isAuthenticated, navigate]);

  useEffect(() => {
    if (!native) return;
    const dispose = initDeepLinks((path) => navigate(path));
    checkMinVersion().then(setGate).catch(() => undefined);
    return dispose;
  }, [native, navigate]);

  // External-link interceptor (App-Link loop fix). Native-only: re-routes off-origin http(s) anchor
  // clicks + window.open() calls through a system Custom Tab (openExternal) so they never navigate
  // the in-app WebView. Same-origin (react-router) links and cbam:// deep-links are left untouched —
  // see externalLinkInterceptor.ts. Mounted once here; the disposer removes the listener and restores
  // window.open on unmount. Web no-ops behind `native` so the desktop build is byte-identical.
  useEffect(() => {
    if (!native) return;
    return installExternalLinkInterceptor();
  }, [native]);

  // Native entry-point guard (P0): the marketing header is suppressed on native and the bottom shell
  // only renders when signed in, so an unauthenticated native user lands on the public homepage with
  // no way to reach login. Bounce them to the partner login screen. Gate on `loading === false` so a
  // signed-in user whose session is being restored from secure storage is NOT bounced mid-restore.
  // Web is unaffected (native-only). Auth/allowed routes are exempt so login + password reset work.
  useEffect(() => {
    if (!native || loading || isAuthenticated) return;
    const allowed = ['/partner/login', '/reset-password'];
    if (allowed.includes(location.pathname)) return;
    navigate('/partner/login', { replace: true });
  }, [native, loading, isAuthenticated, location.pathname, navigate]);

  // Reserve space so the fixed bottom bar never covers page content.
  useEffect(() => {
    if (!active || !isAuthenticated || gate.blocked) return;
    document.body.classList.add('cbam-mobile-shell');
    return () => document.body.classList.remove('cbam-mobile-shell');
  }, [active, isAuthenticated, gate.blocked]);

  // Biometric gate (P0-SEC-02) — included on EVERY native return path (auth'd or not, even under the
  // version gate) so no content, including the login screen, ever paints under a pending lock. Web:
  // `native` is false, so this is always null and the website render output is unchanged.
  const lockOverlay =
    native && locked ? <AppLockOverlay failed={lockFailed} busy={lockBusy} onRetry={tryUnlock} /> : null;

  if (gate.blocked) {
    return (
      <>
        {lockOverlay}
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-3 bg-background p-6 pt-[calc(1.5rem+env(safe-area-inset-top))] pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-center">
          <h1 className="font-heading text-lg font-semibold text-primary">Update required</h1>
          <p className="max-w-xs text-sm text-muted-foreground">{gate.message}</p>
        </div>
      </>
    );
  }

  if (!active || !isAuthenticated) return lockOverlay;

  return (
    <>
      {lockOverlay}
      {/* Status-bar scrim (edge-to-edge / target-36). The WebView draws behind the system status bar
          (ThemeProvider sets overlay:true), so paint a solid, full-width banner exactly the height of
          the top safe-area inset to give the clock/battery icons a clean backdrop and visually separate
          the app chrome from the system bar. bg-card is the app's top-surface token (same as the header
          and bottom bar) so it reads as integrated in BOTH themes — white in light mode, near-black in
          dark mode (the "black banner" the owner asked for) — never a jarring pure-black bar in light.
          Height falls back to 0px pre-viewport-fit, so it's invisible until edge-to-edge is live.
          z-[54]: above page content, below the ProfileSheet button (z-[55], which carries its own top
          inset so the avatar sits ON the scrim) and the OfflineBanner (z-[60]). aria-hidden — purely
          decorative chrome. */}
      <div
        aria-hidden="true"
        className="fixed inset-x-0 top-0 z-[54] h-[env(safe-area-inset-top,0px)] bg-card"
      />
      {/* Global offline indicator — renders nothing while online; mobile shell only (never desktop). */}
      <OfflineBanner />
      <div className="fixed right-0 top-0 z-[55] p-2 pt-[calc(0.5rem+env(safe-area-inset-top))]">
        <ProfileSheet />
      </div>
      <BottomTabBar />
    </>
  );
}
